import { useCallback, useMemo, useState } from 'react';
import { EVENT_REMINDER_PRESETS, EVENT_REMINDER_PROBLEM_KEYS, eventReminderProblem, isPresetLead, type EventReminderAnswer, type EventReminderChannel, type EventReminderDraft, addOutsideGuest, answerWhichSite, chooseLocation, chooseSite, Color, emptyEvent, emptyOutsideGuest, EVENT_MODAL_TYPES, eventProblems, locationScope, needsSeriesScope, needsWhichSite, outsideGuestLabel, outsideGuestProblem, PLACE_SEARCH_LIMIT, placeMatches, recurrenceRule, RECURRENCES, removeOutsideGuest, rendersBand, revealedField, SERIES_SCOPES, siteRequirementsBand, Size, t, timeZoneFor, zoneNoteNeeded, type EventDraft, type EventModalType, type InternalAttendee, type OutsideGuest, type OutsideGuestProblem, type Recurrence, type ResolveSiteCascade, type SearchFn, type SeriesScope, type StringKey, type SuggestionRow, type MapsLinkAnswer, linkStreetLine, placeAtStreetLine } from '@bms2/shared';
import { Button, Checkbox, Chips, DatePicker, Dropdown, PhoneInput, RadioGroup, Select, TextInput, Textarea } from '../controls';
import { Icon } from '../Icon';
import { AlertBanner } from '../controls/AlertBanner';
import { Modal } from './Modal';
import { useAddNew } from '../../pages/records/AddNewHost';
import { MapsLinkPaste } from '../../pages/records/MapsLinkPaste';
import styles from './modal.module.css';

/**
 * The Add/Edit Event modal (piece 5; decisions 0118, 0134, 0180). It is the
 * only event editor in the plan, which is why every rule about what an event
 * may say is kept here: an unasked series-scope question would edit one
 * occurrence and a whole series by the same code path.
 *
 * The Site Requirements band is resolved through the resolver the modal is
 * handed, never by walking the Company-to-occupancy cascade itself, and it
 * never blocks Save — the gate belongs to the Work Order, the only context
 * where the system knows the visit is work rather than a meeting in the front
 * office.
 */
export const eventModalDocumentedStates = ['physical', 'online', 'phone', 'withBand', 'series'] as const;

/** The words a guest's held answer reads in, the same four the Attendee Avatar names. */
const RSVP_KEYS: Record<'NeedsAction' | 'Accepted' | 'Declined' | 'Tentative', StringKey> = {
  NeedsAction: 'avatar.needsAction',
  Accepted: 'avatar.accepted',
  Declined: 'avatar.declined',
  Tentative: 'avatar.tentative',
};

/** What stopped a guest being added, in the words the person reads. */
const GUEST_PROBLEM_KEYS: Record<OutsideGuestProblem, StringKey> = {
  name: 'event.outsideProblem.name',
  email: 'event.outsideProblem.email',
  phone: 'event.outsideProblem.phone',
  duplicate: 'event.outsideProblem.duplicate',
};

export interface PlaceOption {
  id: string;
  label: string;
  /** The type-ahead's second line: a Site's street and town, a Location's occupants (decision 0330). */
  detail?: string | null;
  kind: 'site' | 'location';
  timeZone: string | null;
  /** For a Location, the Sites the cascade resolves it to. */
  resolvesToSiteIds?: readonly string[];
  /** The companies occupying the place, so the field sorts the tagged Company's own first (piece 4). */
  companyIds?: readonly string[];
}

/** The search behind the Place type-ahead: the typed words in, the matching places out (decision 0330). */
export type PlaceSearchFn = (query: string, signal: AbortSignal) => Promise<PlaceOption[]>;

/**
 * The Place field's own search over a list it already holds, for the harness
 * and for a caller with no server behind it. The matcher is the server's, so
 * the two answer alike.
 */
export function localPlaceSearch(places: readonly PlaceOption[]): PlaceSearchFn {
  return async (query: string) => places.filter((place) => placeMatches(query, [place.label, place.detail])).slice(0, PLACE_SEARCH_LIMIT);
}

/**
 * 🔨 The LOCATION field searches every place and sorts the tagged Company's
 * own to the top; tagged to a Work Order, Invoice or Bill it offers that
 * Company's alone (piece 4, `locationScope`). 💰 The list stood in the order
 * the API returned it, so a company's own sites sat wherever the alphabet put
 * them (found while building, 2026-09-09).
 */
export function orderPlaces(places: readonly PlaceOption[], taggedRecordType: string | null | undefined, taggedCompanyId: string | null | undefined): PlaceOption[] {
  if (!taggedCompanyId) return [...places];
  const own = places.filter((place) => (place.companyIds ?? []).includes(taggedCompanyId));
  if (locationScope(taggedRecordType) === 'taggedCompanyOnly') return own;
  return [...own, ...places.filter((place) => !own.includes(place))];
}

export interface EventModalProps {
  open: boolean;
  subtitle?: string;
  initial?: EventDraft;
  ownerPersonId: string;
  organizerTimeZone: string;
  /** The places already known to the modal: an existing event's own, and the which-site answers. */
  places: readonly PlaceOption[];
  /**
   * The Place type-ahead's search (decision 0330): typed text matched against
   * Site names, Location streets and towns and Company names. Absent, the
   * field searches `places` itself.
   */
  searchPlaces?: PlaceSearchFn;
  attendees: readonly InternalAttendee[];
  /**
   * The names of people the draft carries who are not on the internal list, by
   * person: an edited event's own attendees and a record's contact (T362). A
   * pill for any of them reads the name, never the id.
   */
  attendeeNames?: Readonly<Record<string, string>>;
  /** Declared in Company, Person, Location; the modal is handed it and calls it. */
  resolveSiteCascade: ResolveSiteCascade;
  /** The Company search behind the optional Company Dropdown. */
  searchCompanies: SearchFn;
  /** The people search behind the "Add attendee…" Dropdown on the chips. */
  searchPeople: SearchFn;
  /**
   * Add New on the Place type-ahead (decision 0348): the host opens the + Add
   * Site combo modal and hands the new Site back through `accept`, which
   * picks it as the place. Absent, the modal's own Add New host opens the same
   * combo window (T311; decision 0874).
   */
  onAddNewPlace?: (accept: (place: PlaceOption) => void, fromLink?: MapsLinkAnswer) => void;
  /**
   * Reads a pasted Google Maps link for its place (decision 1600). Given, the
   * Place field offers Paste a Maps link: a place on file at the answer's
   * address is picked, and where none stands Add New opens Add Site with step
   * one filled from the link. Absent, the field offers no paste.
   */
  readMapsLink?: (link: string) => Promise<MapsLinkAnswer | null>;
  /**
   * 🔒 The viewer's own reminder and what the row may offer them (decision
   * 1081), read by the host. Absent or still loading, the row is not drawn and
   * the save leaves any stored reminder as it stands.
   */
  reminder?: EventReminderAnswer | null;
  /** Set when editing an existing row, so the Footer offers Cancel meeting. */
  existing?: { parentEventId?: string | null; recurrenceRule?: string | null; cancelledAt?: string | null } | null;
  onSave: (event: EventDraft, scope: SeriesScope | null) => void;
  onDelete?: (scope: SeriesScope | null) => void;
  /** 🔨 Reinstating is why the row is kept; it clears the stamp (decision 0262). */
  onReinstate?: () => void;
  onCancel: () => void;
  openerId?: string | null;
}

export function EventModal({
  open,
  subtitle,
  initial,
  ownerPersonId,
  organizerTimeZone,
  places,
  searchPlaces,
  attendees,
  attendeeNames,
  resolveSiteCascade,
  searchCompanies,
  searchPeople,
  onAddNewPlace,
  readMapsLink,
  existing = null,
  reminder = null,
  onSave,
  onDelete,
  onReinstate,
  onCancel,
  openerId = null,
}: EventModalProps) {
  const [draft, setDraft] = useState<EventDraft>(initial ?? emptyEvent(ownerPersonId, organizerTimeZone));
  const [pendingSites, setPendingSites] = useState<readonly string[]>([]);
  /** The guest being typed; the list itself lives on the draft (decision 0596). */
  const [guest, setGuest] = useState<OutsideGuest>(emptyOutsideGuest());
  const [guestProblem, setGuestProblem] = useState<OutsideGuestProblem | null>(null);
  const removeGuestLabel = (name: string) => `${t('event.outsideRemove')} ${name}`;
  const [scope, setScope] = useState<SeriesScope>('thisOne');
  const set = <K extends keyof EventDraft>(field: K, value: EventDraft[K]) => setDraft((d) => ({ ...d, [field]: value }));

  const revealed = revealedField(draft.eventType);
  /**
   * The places the type-ahead has answered with, by id, so a picked row is
   * known in full — its kind, its zone and the Sites behind it — without a
   * second call. The list the modal was handed stands behind them.
   */
  const [found, setFound] = useState<ReadonlyMap<string, PlaceOption>>(() => new Map());
  /**
   * 🔒 The modal's own Add New host (T311; decision 0874): Add New Person on the
   * attendees adds the saved person as a chip, and Add New on Linked To asks
   * which kind of record first. The names of people made here are held so their
   * chips read a name rather than an id.
   *
   * 🔒 So are the names of people picked from the search who are not on the
   * internal list (T362): a customer's contact picked on a Deal's event joined
   * the draft by id alone, and the pill drew the id.
   */
  const addNew = useAddNew();
  const [madeNames, setMadeNames] = useState<Readonly<Record<string, string>>>(() => ({ ...(attendeeNames ?? {}) }));
  const knownPlace = useCallback((id: string | null | undefined) => (id ? (found.get(id) ?? places.find((p) => p.id === id) ?? null) : null), [found, places]);
  const taggedCompanyId = draft.taggedRecordType === 'CompanyRecord' ? draft.taggedRecordId : null;
  const placeRows = useMemo<SearchFn>(() => {
    const search = searchPlaces ?? localPlaceSearch(places);
    return async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
      const rows = await search(query, signal);
      setFound((was) => new Map([...was, ...rows.map((row): [string, PlaceOption] => [row.id, row])]));
      // 🔨 The tagged Company's own places stand first, and a Work Order's tag offers that Company's alone (piece 4, `locationScope`).
      return orderPlaces(rows, draft.taggedRecordType, taggedCompanyId).map((row) => ({ id: row.id, line1: row.label, ...(row.detail ? { line2: row.detail } : {}) }));
    };
  }, [searchPlaces, places, draft.taggedRecordType, taggedCompanyId]);
  const chosenPlace = knownPlace(draft.place.siteId ?? draft.place.locationId);
  const siteId = draft.place.siteId;
  const resolved = useMemo(() => (siteId ? resolveSiteCascade(siteId) : null), [siteId, resolveSiteCascade]);
  const showsBand = rendersBand(draft.eventType, resolved);
  // The band checks the attendees this event actually carries, not everyone
  // the page could offer: an unmet requirement is a fact about who is going.
  const chosen = attendees.filter((person) => draft.attendeePersonIds.includes(person.personId));
  const band = showsBand && resolved ? siteRequirementsBand(resolved, chosen) : null;
  const asksScope = existing ? needsSeriesScope(existing) : false;
  const problems = eventProblems(draft);

  /**
   * 🔒 The Reminder row (decision 1081): the draft holds it once touched, and
   * until then the stored one stands. Custom holds its typed text apart, so an
   * empty box is a problem named rather than a lead of nothing.
   */
  const reminderShown = reminder !== null;
  const reminderValue: EventReminderDraft | null = draft.reminder !== undefined ? draft.reminder : (reminder?.reminder ?? null);
  const [customLead, setCustomLead] = useState<string | null>(null);
  const leadMode = customLead !== null ? 'custom' : reminderValue === null ? 'off' : isPresetLead(reminderValue.leadMinutes) ? String(reminderValue.leadMinutes) : 'custom';
  const reminderChannel: EventReminderChannel = reminderValue?.channel ?? 'Email';
  const reminderProblem = reminderShown ? eventReminderProblem(reminderValue, { channels: reminder.channels }) : null;
  const setReminder = (next: EventReminderDraft | null) => set('reminder', next);
  const chooseLead = (mode: string) => {
    if (mode === 'off') {
      setCustomLead(null);
      setReminder(null);
    } else if (mode === 'custom') {
      const typed = reminderValue && !isPresetLead(reminderValue.leadMinutes) ? String(reminderValue.leadMinutes) : '';
      setCustomLead(typed);
      setReminder({ leadMinutes: typed === '' ? Number.NaN : Number(typed), channel: reminderChannel });
    } else {
      setCustomLead(null);
      setReminder({ leadMinutes: Number(mode), channel: reminderChannel });
    }
  };

  const pickPlace = (id: string) => pickPlaceOption(knownPlace(id));
  /** What the paste found, said beneath the field: a place picked, or Add Site opened. */
  const [linkNote, setLinkNote] = useState<string | null>(null);
  /** Add New on the Place field, the link's answer handed on where one was read (decisions 0348, 1600). */
  const addNewPlace = (fromLink?: MapsLinkAnswer) =>
    onAddNewPlace
      ? onAddNewPlace(acceptNewPlace, fromLink)
      : addNew.open({ kind: 'site', ...(fromLink ? { fromLink } : {}) }, (row) => acceptNewPlace({ id: row.id, label: row.line1, kind: 'site', timeZone: null }));
  /**
   * 🔒 A pasted link names a place (decision 1600): the answer's street line is
   * searched among the places the field already offers, a place there is picked,
   * and where none stands Add Site opens filled from the link. 🔨 Nothing typed
   * becomes a place: the event still names a Site or a Location by id.
   */
  const placeFromLink = async (answer: MapsLinkAnswer) => {
    const line = linkStreetLine(answer);
    const search = searchPlaces ?? localPlaceSearch(places);
    const rows = line ? orderPlaces(await search(line, new AbortController().signal), draft.taggedRecordType, taggedCompanyId) : [];
    const there = line ? placeAtStreetLine(rows, line) : null;
    if (there) {
      setFound((was) => new Map([...was, [there.id, there]]));
      pickPlaceOption(there);
      setLinkNote(t('mapsLink.placePicked'));
      return;
    }
    setLinkNote(t('mapsLink.placeNew'));
    addNewPlace(answer);
  };
  /** A Site created from the footer is known to nobody yet: it joins the found map and is picked at once (decision 0348). */
  const acceptNewPlace = (place: PlaceOption) => {
    setFound((was) => new Map([...was, [place.id, place]]));
    pickPlaceOption(place);
  };
  const pickPlaceOption = (place: PlaceOption | null) => {
    if (!place) {
      set('place', { siteId: null, locationId: null });
      setPendingSites([]);
      return;
    }
    if (place.kind === 'site') {
      setPendingSites([]);
      setDraft((d) => ({ ...d, place: chooseSite(place.id), timeZone: timeZoneFor(d.eventType, place.timeZone, organizerTimeZone) }));
      return;
    }
    const resolvesTo = place.resolvesToSiteIds ?? [];
    if (needsWhichSite(resolvesTo)) {
      setPendingSites(resolvesTo);
      setDraft((d) => ({ ...d, place: chooseLocation(place.id), timeZone: timeZoneFor(d.eventType, place.timeZone, organizerTimeZone) }));
      return;
    }
    setPendingSites([]);
    // A Location resolving to exactly one Site keeps the Location as the place
    // and shows that Site's band; a Location with no Site behind it is a valid
    // place with no band, because there is no site to have requirements.
    const only = resolvesTo[0] ?? null;
    setDraft((d) => ({
      ...d,
      place: only ? { siteId: only, locationId: place.id } : chooseLocation(place.id),
      timeZone: timeZoneFor(d.eventType, place.timeZone, organizerTimeZone),
    }));
  };

  /** 🔨 Add puts the typed guest on the list and clears the three fields; Save never adds a half-typed one. */
  const addGuest = () => {
    const problem = outsideGuestProblem(guest, draft.outsideContacts);
    setGuestProblem(problem);
    if (problem !== null) return;
    set('outsideContacts', addOutsideGuest(draft.outsideContacts, guest));
    setGuest(emptyOutsideGuest());
  };

  const save = () => {
    if (problems.length > 0 || reminderProblem !== null) return;
    // The Phone Input stores E.164 with its country code (decision 0340); an empty control stores null.
    const callNumber = draft.callNumber?.trim() ? draft.callNumber.trim() : null;
    const callNumberExtension = draft.callNumberExtension?.trim() ? draft.callNumberExtension.trim() : null;
    // 🔨 An untouched row leaves the stored reminder as it stands (decision 1081); a series takes one as a plain event does (decision 1606).
    const { reminder: touched, ...rest } = draft;
    const reminderToSave = !reminderShown ? undefined : touched;
    onSave({ ...rest, callNumber, callNumberExtension, ...(reminderToSave === undefined ? {} : { reminder: reminderToSave }) }, asksScope ? scope : null);
    onCancel();
  };

  const actions = [
    { kind: 'primary' as const, label: t('event.save'), onAct: save, disabled: problems.length > 0 || reminderProblem !== null },
    { kind: 'cancel' as const, label: t('modal.cancel'), onAct: onCancel },
    ...secondaryAction(),
  ];

  /**
   * 🔨 A live meeting offers Cancel meeting, which confirms plainly because it
   * can be undone (decision 0262). A cancelled one offers nothing here and
   * carries Reinstate on its banner instead.
   */
  function secondaryAction() {
    if (!existing) return [];
    // A cancelled meeting offers Reinstate on its banner, not in this slot:
    // bringing a meeting back is not a destructive action and is not drawn red.
    if (existing.cancelledAt) return [];
    if (!onDelete) return [];
    return [
      {
        kind: 'destructiveSecondary' as const,
        label: t('event.cancel'),
        onAct: () => {
          onDelete(asksScope ? scope : null);
          onCancel();
        },
        confirm: {
          title: t('event.cancelTitle'),
          consequence: { body: t('event.cancelBody') },
          confirmLabel: t('event.cancelConfirm'),
        },
      },
    ];
  }

  const modalProps = { open, title: existing ? t('event.editTitle') : t('event.title'), onCancel, openerId, dataModal: 'event', actions, ...(subtitle === undefined ? {} : { subtitle }) };

  return (
    <Modal {...modalProps}>
      {existing?.cancelledAt && onReinstate ? (
        <AlertBanner
          tone="warning"
          message={t('event.cancelledBanner')}
          action={{ label: t('event.reinstate'), onAction: () => { onReinstate(); onCancel(); } }}
        />
      ) : null}
      <TextInput size={Size.Large} label={t('event.subject')} value={draft.subject} onChange={(v) => set('subject', v)} required />

      {/* Figure 52's order: Title, Type, Date and times, the type's own field, Attendees, Linked To; the type control is a row that wraps (product owner, 2026-09-10). */}
      <RadioGroup
        name="event-type"
        legend={t('event.type')}
        orientation="horizontal"
        options={EVENT_MODAL_TYPES.map((type) => ({ value: type, label: t(`event.type.${type.toLowerCase()}` as 'event.type.physical') }))}
        value={draft.eventType}
        onChange={(value) => {
          const next = value as EventModalType;
          setDraft((d) => ({ ...d, eventType: next, timeZone: timeZoneFor(next, chosenPlace?.timeZone ?? null, organizerTimeZone) }));
        }}
      />

      <div className={styles.pair} data-event-field="when">
        <DatePicker size={Size.Small}
          label={t('event.start')}
          value={draft.startsAt}
          onChange={(v) => set('startsAt', v)}
          mode={draft.isAllDay ? 'date' : 'datetime'}
          required
          recordTimeZone={draft.timeZone}
        />
        <DatePicker size={Size.Small}
          label={t('event.end')}
          value={draft.endsAt}
          onChange={(v) => set('endsAt', v)}
          mode={draft.isAllDay ? 'date' : 'datetime'}
          required
          recordTimeZone={draft.timeZone}
        />
      </div>
      <Checkbox label={t('event.allDay')} checked={draft.isAllDay} onChange={(v) => set('isAllDay', v)} />

      {revealed === 'place' ? (
        /*
         * 🔨 The place is a type-ahead over the system's own Sites and Locations
         * (decision 0330): typed text matches a Site's name, a Location's street
         * or town, or the Company occupying it, and a row names the place with
         * its address beneath. Nothing typed becomes a place by itself: an
         * event's place is a Site or a Location (decision 0180), never free text.
         */
        <div data-event-field="place" data-place-id={draft.place.locationId ?? draft.place.siteId ?? ''}>
          <Dropdown size={Size.Medium}
            label={t('event.place')}
            entityLabel={t('event.placeEntity')}
            placeholder={t('event.placePlaceholder')}
            search={placeRows}
            value={chosenPlace?.label ?? null}
            onSelect={(row) => pickPlace(row.id)}
            onAddNew={() => addNewPlace()}
          />
          {readMapsLink ? <MapsLinkPaste read={readMapsLink} onPlace={(answer) => void placeFromLink(answer)} note={linkNote} filledLine={null} onCleared={() => setLinkNote(null)} /> : null}
        </div>
      ) : null}
      {revealed === 'meetingLink' ? (
        <TextInput size={Size.Large} label={t('event.meetingLink')} value={draft.meetingLink ?? ''} onChange={(v) => set('meetingLink', v)} type="url" />
      ) : null}
      {revealed === 'callNumber' ? (
        <div data-event-field="callNumber">
          {/* 🔨 The number types through the Phone Input and is stored E.164 with its country code, +1 unless another is picked (decision 0340); the server refuses what it cannot place. */}
          <PhoneInput size={Size.Medium} label={t('event.callNumber')} value={draft.callNumber ?? ''} onChange={(v) => set('callNumber', v)} name="callNumber" />
          {/* 🔨 The extension sits beside the number and never inside it: a bridge answers, then asks for digits. */}
          <TextInput size={Size.Medium} label={t('event.callNumberExtension')} value={draft.callNumberExtension ?? ''} onChange={(v) => set('callNumberExtension', v)} />
          <p className={styles.fieldLabel}>{t('event.callNumberHint')}</p>
        </div>
      ) : null}

      {pendingSites.length > 1 ? (
        <div className={styles.band} data-event-which-site="true">
          <p className={styles.bandHeading}>{t('event.whichSite')}</p>
          {pendingSites.map((id) => (
            <button
              key={id}
              type="button"
              className={styles.tool}
              data-which-site={id}
              onClick={() => {
                setDraft((d) => ({ ...d, place: answerWhichSite(id) }));
                setPendingSites([]);
              }}
            >
              {knownPlace(id)?.label ?? id}
            </button>
          ))}
        </div>
      ) : null}

      {/* Attendees are removable chips plus an "Add attendee…" Dropdown, and
          the Outside contact field takes anyone who is not a Person record. */}
      <div data-event-field="attendees">
        <Chips
          label={t('event.attendees')}
          entityLabel={t('event.person')}
          value={draft.attendeePersonIds.map((id) => ({ id, label: attendees.find((p) => p.personId === id)?.name ?? madeNames[id] ?? id }))}
          onChange={(next) => {
            // 🔒 A picked person's name is held with their id, so their pill reads the name (T362).
            setMadeNames((held) => ({ ...held, ...Object.fromEntries(next.filter((chip) => chip.label !== chip.id && !(chip.id in held)).map((chip) => [chip.id, chip.label])) }));
            set('attendeePersonIds', next.map((chip) => chip.id));
          }}
          search={searchPeople}
          onAddNew={() => addNew.open({ kind: 'record', recordType: 'PersonRecord' }, (row) => {
            setMadeNames((held) => ({ ...held, [row.id]: row.line1 }));
            setDraft((d) => (d.attendeePersonIds.includes(row.id) ? d : { ...d, attendeePersonIds: [...d.attendeePersonIds, row.id] }));
          })}
        />
      </div>
      {/*
        🔒 Outside contacts are a list, not a box (decision 0596): a name, an
        email and a phone with an Add button, each guest then a removable chip
        carrying the answer their row holds. 💰 One free-text box wrote one row
        into a table that has always held many (product owner, 2026-09-20).
      */}
      <div data-event-field="outsideContacts">
        <p className={styles.fieldLabel}>{t('event.outsideContact')}</p>
        <div className={styles.guestFields}>
          <TextInput size={Size.Medium} label={t('event.outsideName')} value={guest.name} onChange={(v) => setGuest((g) => ({ ...g, name: v }))} />
          <TextInput size={Size.Medium} label={t('event.outsideEmail')} value={guest.email ?? ''} onChange={(v) => setGuest((g) => ({ ...g, email: v === '' ? null : v }))} type="email" />
          {/* The number types through the Phone Input and is stored E.164 with its country code (decision 0340). */}
          <PhoneInput size={Size.Medium} label={t('event.outsidePhone')} value={guest.phone ?? ''} onChange={(v) => setGuest((g) => ({ ...g, phone: v === '' ? null : v }))} name="outsidePhone" />
          <span className={styles.guestAdd}>
            <Button size={Size.Small} color={Color.Inactive} text={[t('event.outsideAdd')]} actions={[addGuest]} />
          </span>
        </div>
        {/* 🔒 The guests read under the fields that add them (decision 1451). */}
        {draft.outsideContacts.length > 0 ? (
          <ul className={styles.guests} data-outside-guests={String(draft.outsideContacts.length)}>
            {draft.outsideContacts.map((row) => (
              <li key={row.name} className={styles.guestChip} data-outside-guest={row.name}>
                <span className={styles.guestName}>{outsideGuestLabel(row)}</span>
                {/* 🔨 The answer is drawn, never typed: it is the guest's to give (decision 0596). */}
                <span className={styles.guestRsvp} data-outside-rsvp={row.attending ?? 'NeedsAction'}>{t(RSVP_KEYS[row.attending ?? 'NeedsAction'])}</span>
                <button
                  type="button"
                  className={styles.guestRemove}
                  aria-label={removeGuestLabel(row.name)}
                  onClick={() => set('outsideContacts', removeOutsideGuest(draft.outsideContacts, row.name))}
                  data-outside-remove={row.name}
                >
                  <Icon meaning="remove" size="inline" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {guestProblem !== null ? <p className={styles.guestProblem} data-outside-problem={guestProblem}>{t(GUEST_PROBLEM_KEYS[guestProblem])}</p> : null}
        <p className={styles.fieldLabel}>{t('event.outsideHint')}</p>
      </div>

      {/* Offered and optional: an internal event such as a team meeting saves
          with the tag columns empty. An existing event shows what it is linked to. */}
      <div data-event-field="company" data-tagged-company={draft.taggedRecordId ?? ''}>
        <Dropdown size={Size.Medium}
          label={t('event.linkedTo')}
          entityLabel={t('event.companyEntity')}
          search={searchCompanies}
          value={draft.taggedRecordLabel ?? null}
          onSelect={(row) => {
            // The id carries its record type in front, the way the linked search hands it over (decision 0275); a plain id is a Company.
            const colon = row.id.indexOf(':');
            const taggedRecordType = colon > 0 ? row.id.slice(0, colon) : 'CompanyRecord';
            const taggedRecordId = colon > 0 ? row.id.slice(colon + 1) : row.id;
            setDraft((d) => ({ ...d, taggedRecordId, taggedRecordType, taggedRecordLabel: row.line1 }));
          }}
          onAddNew={() => addNew.open({ kind: 'choose', types: ['CompanyRecord', 'PersonRecord', 'LocationRecord', 'Deal'], typedIds: true }, (row) => {
            const colon = row.id.indexOf(':');
            setDraft((d) => ({ ...d, taggedRecordType: row.id.slice(0, colon), taggedRecordId: row.id.slice(colon + 1), taggedRecordLabel: row.line1 }));
          })}
        />
      </div>

      <Select size={Size.Medium}
        label={t('event.recurrence')}
        value={draft.recurrence}
        onChange={(next) => set('recurrence', next as Recurrence)}
        options={RECURRENCES.map((rule) => ({ value: rule, label: t(`event.recurrence.${rule.toLowerCase()}` as 'event.recurrence.none') }))}
        dataAttribute={{ 'data-event-field': 'recurrence' }}
      />
      {draft.recurrence !== 'None' ? (
        <DatePicker size={Size.Small} label={t('event.recurrenceEnd')} value={draft.recurrenceEndDate ?? ''} onChange={(v) => set('recurrenceEndDate', v === '' ? null : v)} />
      ) : null}
      {/*
        🔨 The zone is named only where the event is held in one that is not the
        viewer's own, the way every other surface names it (decision 0261). 💰 The
        line read "Times are in America/Chicago" on every event, including one in
        the reader's own zone, which is what prompted the product owner to ask
        why everything was stored in Chicago (2026-09-20).
      */}
      <p className={styles.fieldLabel} data-event-rule={recurrenceRule(draft.recurrence, draft.recurrenceEndDate, draft.timeZone) ?? 'none'} data-event-zone={draft.timeZone}>
        {zoneNoteNeeded(organizerTimeZone, draft.timeZone) ? `${t('event.zone')} ${draft.timeZone}` : ''}
      </p>

      <Textarea size={Size.Large} label={t('event.notes')} value={draft.notes ?? ''} onChange={(v) => set('notes', v === '' ? null : v)} />

      {reminderShown ? (
        /*
          🔒 The Reminder row (decision 1081; T425): Off or a lead time, and Email
          or Text. Text stands greyed with its reason unless the seam reports an
          active adapter and the person has a mobile number. A repeating event
          takes one too, sent before each occurrence (decision 1606).
        */
        <div data-event-field="reminder" data-reminder-mode={leadMode} data-reminder-channel={reminderValue ? reminderChannel : 'none'}>
          {/* As wide as its longest choice, 1 day before or 15 minutes before, so none is cut off. */}
          <Select chars={20}
            label={t('event.reminder')}
            value={leadMode}
            onChange={chooseLead}
            options={[
              { value: 'off', label: t('event.reminder.off') },
              ...EVENT_REMINDER_PRESETS.map((lead) => ({ value: String(lead), label: t(`event.reminder.lead${lead}` as 'event.reminder.lead15') })),
              { value: 'custom', label: t('event.reminder.custom') },
            ]}
            dataAttribute={{ 'data-reminder-lead': 'true' }}
          />
          {leadMode === 'custom' ? (
            <TextInput size={Size.Small}
              label={t('event.reminder.minutes')}
              value={customLead ?? (reminderValue ? String(reminderValue.leadMinutes) : '')}
              onChange={(v) => {
                setCustomLead(v);
                setReminder({ leadMinutes: v.trim() === '' ? Number.NaN : Number(v), channel: reminderChannel });
              }}
            />
          ) : null}
          {reminderValue !== null ? (
            <RadioGroup
              name="event-reminder-channel"
              legend={t('event.reminder.channel')}
              orientation="horizontal"
              options={[
                { value: 'Email', label: t('event.reminder.email') },
                { value: 'Text', label: t('event.reminder.text'), disabled: !reminder.channels.text.offered },
              ]}
              value={reminderChannel}
              onChange={(v) => setReminder({ leadMinutes: reminderValue.leadMinutes, channel: v as EventReminderChannel })}
            />
          ) : null}
          {reminderValue !== null && !reminder.channels.text.offered && reminder.channels.text.reason ? (
            <p className={styles.fieldLabel} data-reminder-reason={reminder.channels.text.reason}>{t(EVENT_REMINDER_PROBLEM_KEYS[reminder.channels.text.reason])}</p>
          ) : null}
          {reminderProblem !== null && reminderProblem !== 'noTextService' && reminderProblem !== 'noMobile' ? (
            <p className={styles.guestProblem} data-reminder-problem={reminderProblem}>{t(EVENT_REMINDER_PROBLEM_KEYS[reminderProblem])}</p>
          ) : null}
        </div>
      ) : null}

      {asksScope ? (
        <RadioGroup
          name="event-scope"
          legend={t('event.scope')}
          options={SERIES_SCOPES.map((s) => ({ value: s, label: t(`event.scope.${s}` as 'event.scope.thisOne') }))}
          value={scope}
          onChange={(v) => setScope(v as SeriesScope)}
        />
      ) : null}

      {band ? (
        <section className={styles.band} data-site-requirements="true">
          <h3 className={styles.bandHeading}>{t('event.requirements')}</h3>
          {band.bring.length > 0 ? (
            <>
              <p className={styles.bandHeading}>{t('event.bring')}</p>
              <ul className={styles.bandList} data-band-bring="true">
                {band.bring.map((row) => (
                  <li key={row.id}>{row.name}</li>
                ))}
              </ul>
            </>
          ) : null}
          {band.verified.map((row) => (
            <div key={row.requirement.id} data-band-verified={row.requirement.id}>
              <p className={styles.bandHeading}>{row.requirement.name}</p>
              <ul className={styles.bandList}>
                {row.attendees.map((person) => (
                  <li key={person.personId} className={person.met ? '' : styles.unmet} data-band-met={person.met ? 'true' : 'false'}>
                    {person.name}
                    {person.met && person.expiresOn ? ` — ${t('event.expires')} ${person.expiresOn}` : ''}
                    {person.met ? '' : ` — ${t('event.notHeld')}`}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {band.policies.length > 0 ? (
            <ul className={styles.bandList} data-band-policies="true">
              {band.policies.map((policy) => (
                <li key={policy.id}>{policy.title}</li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
      {addNew.host}
    </Modal>
  );
}
